import React from 'react';
import { connect } from 'react-redux';
import { map } from 'lodash';
import { Card, NativeTable, Paragraph, Text } from 'elmo-elements';
import {
  AccountTreeSelector,
  LanguagePreferences,
  UserProfileFields,
  UserRole,
} from 'type';
import { getRoleName } from 'lib/helpers';
import { StoreState } from 'state/types';
import { getAccountTree } from 'state/AccountTree';
import { getLangPreferences } from 'state/Account';
import { hasPermissionSelector, getCurrentUserPermissions } from 'state/Auth';
import {
  BOX_USER_PROFILE_ADD_ROLE_REQUEST,
  BOX_USER_PROFILE_ROLES_MODAL_CLOSE,
  BOX_USER_PROFILE_ROLES_MODAL_OPEN,
  getErrors,
  getIsOpened,
  getIsUpdating,
  getUserProfile,
  getUserRoles,
} from 'state/Users/UserProfile';
import {
  RoleModal,
  RoleModalSubmitPayload,
} from '../../../../../../components';
import { EditButton } from '../';
import { FormattedErrors } from 'type';
import { NewPayRatesAlert } from './components/NewPayRatesAlert';
import './style.scss';

type StateProps = {
  roles: UserRole[];
  tree: AccountTreeSelector;
  isOpened: boolean;
  isUpdating: boolean;
  errors: FormattedErrors;
  langPreferences: LanguagePreferences;
  userProfile: UserProfileFields;
  hasPayRateEditPermission: boolean;
};

type DispatchProps = {
  closeModal: () => void;
  openModal: () => void;
  onFormSubmit: (data: RoleModalSubmitPayload) => void;
};

type Props = StateProps & DispatchProps;

export const RoleComponent = ({
  langPreferences,
  openModal,
  isOpened,
  isUpdating,
  closeModal,
  onFormSubmit,
  errors,
  roles,
  tree,
  userProfile,
  hasPayRateEditPermission,
}: Props) => {
  
  const getPayRate = (userRole: UserRole): string => {
    if (userRole.pay_rate !== null && userRole.pay_rate !== undefined) {
      return `£${userRole.pay_rate.toFixed(2)} p/h`;
    }

    return 'Unassigned';
  };

  return (
    <Card
      isFullHeight={true}
      heading="Role"
      option={userProfile.is_active && <EditButton onClick={openModal} ariaLabel="Edit" />}
    >
      {userProfile.shouldShowNewPayRatesAlert && <NewPayRatesAlert />}

      <NativeTable isFullWidth={true}>
        <NativeTable.Header>
          <NativeTable.Tr>
            <NativeTable.Th width={hasPayRateEditPermission ? '40%' : '50%'}>
              <Text size={'sm'}>Role</Text>
            </NativeTable.Th>
            <NativeTable.Th width={hasPayRateEditPermission ? '20%' : '25%'}>
              <Text size={'sm'}>Department</Text>
            </NativeTable.Th>
            <NativeTable.Th width={hasPayRateEditPermission ? '20%' : '25%'}>
              <Text size={'sm'}>Location</Text>
            </NativeTable.Th>
            {hasPayRateEditPermission && (
              <NativeTable.Th width={'20%'}>
                <Text size={'sm'}>Pay Rate</Text>
              </NativeTable.Th>
            )}
          </NativeTable.Tr>
        </NativeTable.Header>
        <NativeTable.Body>
          {map(roles, (userRole: UserRole, index) => {
            const roleName = getRoleName(userRole, tree.roles, 'role');
            const areaName = getRoleName(userRole, tree.areas, 'area');
            const siteName = getRoleName(userRole, tree.sites, 'site');
            const payRate = getPayRate(userRole);
            const isUnassigned = payRate === 'Unassigned';

            return (
              <NativeTable.Tr
                key={userRole.role_id + userRole.area_id + userRole.site_id}
              >
                <NativeTable.Td>
                  <Paragraph className={`role-value role-value-${index}`}>
                    {roleName}
                    {userRole.is_main && (
                      <span className="role-badge role-badge-primary">Primary</span>
                    )}
                  </Paragraph>
                </NativeTable.Td>
                <NativeTable.Td>
                  <Paragraph className={`department-value department-value-${index}`}>
                    {areaName || '-'}
                  </Paragraph>
                </NativeTable.Td>
                <NativeTable.Td>
                  <Paragraph className={`location-value location-value-${index}`}>
                    {siteName || '-'}
                  </Paragraph>
                </NativeTable.Td>
                {hasPayRateEditPermission && (
                  <NativeTable.Td>
                    <Paragraph className={`pay-rate-value pay-rate-value-${index}`}>
                      {isUnassigned && (
                        <span className="pay-rate-warning-icon">⚠</span>
                      )}
                      <span
                        className={isUnassigned ? 'pay-rate-text-error' : ''}
                      >
                        {payRate}
                      </span>
                    </Paragraph>
                  </NativeTable.Td>
                )}
              </NativeTable.Tr>
            );
          })}
        </NativeTable.Body>
      </NativeTable>

      <RoleModal
        isOpened={isOpened}
        isUpdating={isUpdating}
        onCloseModal={closeModal}
        onFormSubmit={onFormSubmit}
        errors={errors}
        initRoles={roles}
      />
    </Card>
  );
};

const mapStateToProps = (state: StoreState): StateProps => {
  const permission = hasPermissionSelector(state, 'payrate.edit');
  
  return {
    roles: getUserRoles(state),
    tree: getAccountTree(state),
    isOpened: getIsOpened(state, 'rolesModal'),
    isUpdating: getIsUpdating(state),
    errors: getErrors(state),
    langPreferences: getLangPreferences(state),
    userProfile: getUserProfile(state),
    hasPayRateEditPermission: permission,
  };
};

const mapDispatchToProps: DispatchProps = {
  closeModal: BOX_USER_PROFILE_ROLES_MODAL_CLOSE,
  openModal: BOX_USER_PROFILE_ROLES_MODAL_OPEN,
  onFormSubmit: BOX_USER_PROFILE_ADD_ROLE_REQUEST,
};

export const Role = connect(
  mapStateToProps,
  mapDispatchToProps
)(RoleComponent);